import React, { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import {
  Box, Typography, Button, Stack, Paper, Divider, Chip, Slider,
  useTheme, Alert,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
} from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import WarningIcon from '@mui/icons-material/Warning';
import DownloadIcon from '@mui/icons-material/Download';
import MapView from '../MapView';
import { Panel, PanelGroup, PanelResizeHandle } from 'react-resizable-panels';
import { readFileAsText, parseAnyGeoFile, haversineDistance, calculateSpeed } from '../../../utils/geoUtils';
import SEO, { createSoftwareAppSchema } from '../../../components/SEO';
import L from 'leaflet';

function HarshBrakingAnalysis() {
  const theme = useTheme();
  const [features, setFeatures] = useState([]);
  const [error, setError] = useState('');
  const [brakingThreshold, setBrakingThreshold] = useState(15);
  const [accelThreshold, setAccelThreshold] = useState(12);

  const handleUpload = useCallback(async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    try {
      const text = await readFileAsText(file);
      const result = parseAnyGeoFile(text);
      if (result.error || !result.geoJson) throw new Error(result.error || 'Could not parse file');
      const fc = result.geoJson;
      let pts = fc.features.filter(f => f.geometry?.type === 'Point');
      if (pts.length < 2) {
        const lines = fc.features.filter(f => f.geometry?.type === 'LineString');
        if (lines.length > 0) {
          pts = lines[0].geometry.coordinates.map((c, i) => ({ type: 'Feature', geometry: { type: 'Point', coordinates: c }, properties: { index: i } }));
        }
      }
      if (pts.length < 2) throw new Error('Need at least 2 points with speed/timestamp');
      setFeatures(pts);
      setError('');
    } catch (err) {
      setError(err.message);
    }
  }, []);

  const events = useMemo(() => {
    if (features.length < 2) return [];
    const evts = [];

    for (let i = 1; i < features.length; i++) {
      const prev = features[i - 1];
      const curr = features[i];
      const prevTs = prev.properties?.timestamp || prev.properties?.time;
      const currTs = curr.properties?.timestamp || curr.properties?.time;

      let speed1 = prev.properties?.speed ?? prev.properties?.Speed;
      let speed2 = curr.properties?.speed ?? curr.properties?.Speed;

      // Calculate speed from position if not provided
      if ((speed1 === undefined || speed2 === undefined) && prevTs && currTs) {
        speed1 = speed1 ?? calculateSpeed(prev.geometry.coordinates, curr.geometry.coordinates, prevTs, currTs);
        speed2 = speed2 ?? speed1;
      }

      if (speed1 === undefined || speed2 === undefined) continue;

      const dt = prevTs && currTs ? (new Date(currTs) - new Date(prevTs)) / 1000 : 1;
      if (dt <= 0) continue;

      const decel = (speed1 - speed2) / dt * 3.6; // km/h/s
      const accel = (speed2 - speed1) / dt * 3.6;

      if (decel > brakingThreshold) {
        evts.push({
          type: 'braking',
          index: i,
          coordinate: curr.geometry.coordinates,
          speedBefore: speed1,
          speedAfter: speed2,
          rate: decel,
          time: currTs,
        });
      } else if (accel > accelThreshold) {
        evts.push({
          type: 'acceleration',
          index: i,
          coordinate: curr.geometry.coordinates,
          speedBefore: speed1,
          speedAfter: speed2,
          rate: accel,
          time: currTs,
        });
      }
    }
    return evts;
  }, [features, brakingThreshold, accelThreshold]);

  const mapGeoJson = useMemo(() => {
    if (features.length === 0) return null;
    const allFeatures = [];
    // Route line
    const coords = features.map(f => f.geometry.coordinates);
    if (coords.length > 1) {
      allFeatures.push({
        type: 'Feature',
        geometry: { type: 'LineString', coordinates: coords },
        properties: { layer: 'route' },
      });
    }
    // Event markers
    events.forEach(evt => {
      allFeatures.push({
        type: 'Feature',
        geometry: { type: 'Point', coordinates: evt.coordinate },
        properties: { layer: 'event', type: evt.type, rate: evt.rate },
      });
    });
    return { type: 'FeatureCollection', features: allFeatures };
  }, [features, events]);

  const styleFunction = useCallback((feature) => {
    if (feature.properties.layer === 'route') return { color: '#6366f1', weight: 2.5, opacity: 0.6 };
    return {};
  }, []);

  const pointToLayerFn = useCallback((feature, latlng) => {
    if (feature.properties.layer === 'event') {
      const isBraking = feature.properties.type === 'braking';
      return L.circleMarker(latlng, {
        radius: 8,
        fillColor: isBraking ? '#ef4444' : '#f59e0b',
        color: '#fff', weight: 2, fillOpacity: 0.9,
      }).bindTooltip(`${isBraking ? '⚠ Braking' : '⚡ Accel'}: ${feature.properties.rate.toFixed(1)} km/h/s`);
    }
    return L.circleMarker(latlng, { radius: 0 });
  }, []);

  const handleExport = () => {
    const report = {
      summary: { totalEvents: events.length, brakingEvents: events.filter(e => e.type === 'braking').length, accelEvents: events.filter(e => e.type === 'acceleration').length },
      events: events.map(e => ({ ...e })),
    };
    const blob = new Blob([JSON.stringify(report, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = 'harsh_events.json'; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <SEO
        title="Harsh Braking & Acceleration Analysis | Telematics | UmbrellaCo"
        description="Upload GPS traces to automatically detect harsh braking and rapid acceleration events. Set custom thresholds and visualize driving behavior on an interactive map."
        keywords="harsh braking detection, telematics analysis, driving behavior, acceleration events, gps speed analysis"
        url="/spatial-tools/harsh-braking"
        jsonLd={createSoftwareAppSchema(
          'Harsh Braking Analysis',
          'Detect and map harsh braking and acceleration events from GPS traces.',
          'https://umbrellaco.in/spatial-tools/harsh-braking',
          ['Telematics', 'Driving Behavior Analysis', 'Event Detection']
        )}
      />
      <Box sx={{ px: 2, pt: 1, pb: 1, flexShrink: 0 }}>
        <Typography component="h1" variant="h6" fontWeight={700}>Harsh Braking & Acceleration Analysis</Typography>
        <Typography variant="body2" color="text.secondary">
          Upload telematics data to detect and map harsh braking and rapid acceleration events based on customizable rate-of-change thresholds.
        </Typography>
      </Box>
      <PanelGroup direction="horizontal">
        <Panel defaultSize={32} minSize={22}>
          <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'auto', bgcolor: 'background.paper', borderRight: `1px solid ${theme.palette.divider}` }}>
            <Box sx={{ p: 2 }}>
              <Button component="label" variant="contained" fullWidth size="small" startIcon={<UploadFileIcon />}
                sx={{ background: 'linear-gradient(135deg, #ef4444 0%, #f97316 100%)' }}>
                {features.length > 0 ? `Loaded (${features.length} pts)` : 'Upload Trip Data'}
                <input type="file" hidden accept=".geojson,.json,.csv,.tsv,.txt" onChange={handleUpload} />
              </Button>
              {error && <Alert severity="error" sx={{ mt: 1 }}>{error}</Alert>}
            </Box>
            <Divider />
            <Box sx={{ p: 2 }}>
              <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>Thresholds</Typography>
              <Typography variant="body2" sx={{ mb: 0.5 }}>🛑 Harsh Braking (km/h/s)</Typography>
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
                <Slider value={brakingThreshold} onChange={(e, v) => setBrakingThreshold(v)} min={3} max={40} step={1} size="small" sx={{ flex: 1 }}
                  color="error" />
                <Typography variant="caption" fontWeight={600} sx={{ minWidth: 35 }}>{brakingThreshold}</Typography>
              </Stack>
              <Typography variant="body2" sx={{ mb: 0.5 }}>⚡ Harsh Acceleration (km/h/s)</Typography>
              <Stack direction="row" alignItems="center" spacing={1}>
                <Slider value={accelThreshold} onChange={(e, v) => setAccelThreshold(v)} min={3} max={30} step={1} size="small" sx={{ flex: 1 }}
                  color="warning" />
                <Typography variant="caption" fontWeight={600} sx={{ minWidth: 35 }}>{accelThreshold}</Typography>
              </Stack>
            </Box>
            <Divider />
            {events.length > 0 && (
              <Box sx={{ p: 2, flexGrow: 1, overflow: 'auto' }}>
                <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: 1 }}>
                  <WarningIcon fontSize="small" sx={{ color: '#ef4444' }} />
                  <Typography variant="subtitle2" fontWeight={700}>{events.length} Events</Typography>
                </Stack>
                <Stack direction="row" spacing={0.5} sx={{ mb: 1 }}>
                  <Chip size="small" label={`${events.filter(e => e.type === 'braking').length} Braking`} sx={{ bgcolor: '#ef444422', color: '#ef4444', fontWeight: 600 }} />
                  <Chip size="small" label={`${events.filter(e => e.type === 'acceleration').length} Accel`} sx={{ bgcolor: '#f59e0b22', color: '#f59e0b', fontWeight: 600 }} />
                </Stack>
                <TableContainer sx={{ maxHeight: '40vh' }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        {['#', 'Type', 'Rate', 'Speed'].map(h => (
                          <TableCell key={h} sx={{ fontWeight: 700, fontSize: '0.7rem', py: 0.5 }}>{h}</TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {events.slice(0, 50).map((evt, i) => (
                        <TableRow key={i} sx={{ '& td': { py: 0.3, fontSize: '0.7rem' } }}>
                          <TableCell>{evt.index}</TableCell>
                          <TableCell>
                            <Chip size="small" label={evt.type === 'braking' ? '🛑' : '⚡'}
                              sx={{ height: 18, fontSize: '0.65rem', bgcolor: evt.type === 'braking' ? '#ef444422' : '#f59e0b22' }} />
                          </TableCell>
                          <TableCell sx={{ fontWeight: 700, color: evt.type === 'braking' ? '#ef4444' : '#f59e0b' }}>
                            {evt.rate.toFixed(1)} km/h/s
                          </TableCell>
                          <TableCell>{evt.speedBefore.toFixed(0)} → {evt.speedAfter.toFixed(0)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            )}
            {events.length > 0 && (
              <Box sx={{ p: 2, borderTop: `1px solid ${theme.palette.divider}`, flexShrink: 0 }}>
                <Button variant="outlined" fullWidth size="small" startIcon={<DownloadIcon />} onClick={handleExport}>
                  Export Events Report
                </Button>
              </Box>
            )}
          </Box>
        </Panel>
        <PanelResizeHandle style={{ width: '5px', background: theme.palette.divider, cursor: 'col-resize' }} />
        <Panel defaultSize={68} minSize={40}>
          <Box sx={{ height: '100%', width: '100%' }}>
            {mapGeoJson ? (
              <MapView mapId="harsh-braking-map" geoJsonData={mapGeoJson} styleFunction={styleFunction} pointToLayerFn={pointToLayerFn} />
            ) : (
              <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: theme.palette.mode === 'dark' ? 'rgba(0,0,0,0.3)' : 'rgba(0,0,0,0.03)' }}>
                <Stack alignItems="center" spacing={2} sx={{ p: 4, textAlign: 'center' }}>
                  <WarningIcon sx={{ fontSize: 64, color: 'text.disabled' }} />
                  <Typography variant="h6" color="text.secondary">Upload trip data with speed/timestamps</Typography>
                </Stack>
              </Box>
            )}
          </Box>
        </Panel>
      </PanelGroup>
    </Box>
  );
}

export default HarshBrakingAnalysis;
